import type {
  ActorDatabaseHandle,
  ActorTransaction,
} from "@-/dev.improve.haniverse.database.actor";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";

type ActorReader = ActorDatabaseHandle | ActorTransaction;

/**
 * Whether the learner has ever committed a live review. Soft-deleted (undone) rows do not count:
 * undoing the only review returns them to the never-reviewed state.
 */
export async function readHasReviewedAtLeastOnce(db: ActorReader): Promise<boolean> {
  const review = await db.query.userCardReview.findFirst({
    columns: { id: true },
  });
  return review != null;
}

function useHasReviewedAtLeastOnceTableChangeVersion() {
  return useTableChangeVersion(tableNameMatcher(["user_card_review"]));
}

export function useGetHasReviewedAtLeastOnceQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const withTableChangeVersion = useHasReviewedAtLeastOnceTableChangeVersion();
  return () => withTableChangeVersion(["hasReviewedAtLeastOnce", { actorDb }] as const);
}

/** `true` once any live `user_card_review` row exists. */
export const [useHasReviewedAtLeastOnceQueryOptions] = createUseQueryOptions(
  useGetHasReviewedAtLeastOnceQueryKey,
  async ({ queryKey: [_, { actorDb }] }) =>
    readHasReviewedAtLeastOnce(actorDb(import.meta.location)),
);
